/**
 * The one place that talks to the backend.
 *
 * Everything goes through `request`, so authentication failures, network
 * errors and server errors are handled identically everywhere rather than
 * being re-invented per screen.
 */

const BASE = import.meta.env.PROD ? "https://api.vaunt.buttonup.management" : "";

/** An absolute API URL, for the few things the BROWSER fetches rather than
 *  this module - a file download, where we want the browser to stream it
 *  straight to disk.
 *
 *  It has to be absolute. The console is served from vaunt.buttonup.management
 *  and the API from api.vaunt.buttonup.management, so a relative "/api/..."
 *  href is a path on the CONSOLE, which does not exist there - the SPA router
 *  catches it and redirects to the home page, which is exactly what an export
 *  link did. In dev BASE is empty and vite proxies /api, so it works there and
 *  breaks only in production, which is the worst way for it to break.
 *
 *  The session cookie still travels: both hosts sit under buttonup.management,
 *  so this is same-site and a top-level navigation sends it.
 */
export const apiUrl = (path: string) => BASE + path;

/** An audience being put into a campaign in the background. */
export type EnrollmentJob = {
  id: number;
  campaign_id: number;
  campaign_name: string | null;
  status: "queued" | "running" | "done" | "canceled" | "failed";
  matched: number;
  position: number;
  enrolled: number;
  skipped: number;
  done_share: number;
  created_by: string;
  created_at: string;
  finished_at: string | null;
  error: string | null;
};

export type EnrolReply = {
  job_id: number;
  status: EnrollmentJob["status"];
  matched: number;
  enrolled: number;
  skipped: number;
};

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(BASE + path, {
    ...init,
    // the session is a cookie, so every request must carry it
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
  });

  if (res.status === 401) {
    // Not signed in, or access was removed while the tab was open. Send them
    // to sign in rather than showing a broken screen.
    window.location.href = `${BASE}/auth/google/login?next=${encodeURIComponent(
      window.location.href,
    )}`;
    throw new ApiError(401, "Signing in…");
  }

  if (!res.ok) {
    let detail = res.statusText;
    try {
      const d = (await res.json()).detail ?? detail;
      // some refusals carry a structured detail with a sentence to show
      detail = typeof d === "object" && d && "message" in d ? String(d.message) : d;
    } catch {
      /* a non-JSON error body is still an error */
    }
    throw new ApiError(res.status, detail);
  }

  return res.status === 204 ? (undefined as T) : res.json();
}

const get = <T>(p: string) => request<T>(p);
const post = <T>(p: string, body?: unknown) =>
  request<T>(p, { method: "POST", body: body ? JSON.stringify(body) : undefined });
const patch = <T>(p: string, body: unknown) =>
  request<T>(p, { method: "PATCH", body: JSON.stringify(body) });
const del = (p: string) => request<void>(p, { method: "DELETE" });

// ── types, mirroring the API schemas ─────────────────────────
export type Me = {
  id: number;
  email: string;
  name?: string;
  role: string;
  team?: string;
  /** The Activity log is one named person's, not a role's. */
  can_see_activity?: boolean;
  /** The Keys page is too (KEY_OWNERS). */
  can_manage_keys?: boolean;
};

export type Contact = {
  id: number;
  phone_e164: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  state?: string;
  consent_status: string;
  subscriber_status: string;
  responsiveness: string;
  lead_score?: number;
  lead_score_band?: string;
  last_reply_at?: string;
  last_flight_at?: string;
  flight_count: number;
  owner_user_id?: number;

  // present on the detail endpoint only
  external_id?: string;
  five9_contact_id?: string;
  source?: string;
  created_at?: string;
  updated_at?: string;
  city?: string;
  postal_code?: string;
  timezone?: string;
  timezone_source?: string;
  best_send_hour_local?: number;
  consent_source?: string;
  consent_at?: string;
  opted_out_at?: string;
  dnc?: boolean;
  phone_valid?: boolean;
  hard_fail_count?: number;
  first_reply_at?: string;
  last_outbound_at?: string;
  reply_count?: number;
  sends_today?: number;
  sends_7d?: number;
  sends_30d?: number;
  sends_total?: number;
  signup_at?: string;
  auto_dnd_at?: string;
  calls_total?: number;
  calls_connected?: number;
  last_call_at?: string;
  last_call_disposition?: string;
  last_closer_user_id?: number;
  last_offer?: string;
  last_offer_at?: string;
  sale_count?: number;
  lifetime_value_cents?: number;
  appointment_count?: number;
  appointments_kept?: number;
  appointments_no_show?: number;
  next_appointment_at?: string;
  first_reply_campaign_id?: number;
  first_reply_day_offset?: number;
  experiment_bucket?: number;
  team?: string;
  automation_paused_until?: string;
  attrs?: Record<string, unknown>;
};

export type TimelineEntry = {
  at: string;
  kind: "outbound" | "inbound" | "suppressed" | "event" | "call" | "appointment";
  title: string;
  body?: string;
  detail?: string;
  meta: Record<string, unknown>;
};

export type FieldOption = {
  key: string;
  label: string;
  kind: string;
  options: string[];
  /** Contacts holding a value. 0 means nothing writes this field yet, so any
   *  rule using it matches nobody. */
  populated?: number | null;
};

export type RulePreview = {
  matched: number;
  reachable: number;
  excluded: Record<string, number>;
  excluded_total: number;
  sample: Array<Record<string, unknown>>;
};

export type Segment = {
  id: number;
  key: string;
  name: string;
  description?: string;
  definition: Rule;
  last_count?: number;
  last_reachable?: number;
  /** When the two counts above were taken - the save, since nothing
   *  re-counts a saved audience afterwards. */
  last_evaluated_at?: string;
};

export type Template = {
  id: number;
  key: string;
  name: string;
  status: string;
  created_at?: string | null;
  variants: Array<{ id: number; variant_key: string; body: string; status: string; weight: number }>;
  /** Where it is used: a live campaign step (with its day), or a blast. */
  used_by?: Array<{
    kind: "campaign" | "blast";
    id: number;
    name: string;
    day?: number | null;
    campaign_type?: string | null;
  }>;
};

export type CostPreview = {
  characters: number;
  segments: number;
  is_unicode: boolean;
  special_characters: string[];
  cost_usd: number;
  warning?: string;
};

export type Blast = {
  id: number;
  name: string;
  status: string;
  total_matched: number;
  total_recipients: number;
  count_sent: number;
  count_delivered: number;
  count_failed: number;
  count_replied: number;
  rate_per_minute: number;
  scheduled_at?: string | null;
  started_at?: string | null;
  /** Builds its list from whoever matches at scheduled_at, not at creation. */
  build_at_send?: boolean;
  /** sent only when the team has room (volume autopilot) */
  standby?: boolean;
  /** Waiting to build its list at the send time: how many match right now. */
  matching_now?: number | null;
  exclusion_breakdown?: Record<string, number>;
  personalize?: boolean;
  personalize_brief?: string | null;
};

/** One sample draft, with what the guards said about it.
 *
 *  `rejected` is shown rather than hidden. A refusal on screen is the most
 *  reassuring thing on this panel: it is the checks doing visibly what they
 *  will go on doing unattended. */
export type PersonalizedDraft = {
  contact_id: number;
  name: string;
  from: string;
  where_they_stand: string;
  message: string;
  angle: string;
  rejected: string[];
  would_send: boolean;
  characters: number;
  cost_usd: number;
};

export type BlastProgress = {
  id: number;
  name: string;
  status: string;
  total: number;
  sent: number;
  delivered: number;
  failed: number;
  suppressed: number;
  canceled: number;
  remaining: number;
  /** People who replied, not messages. */
  replied: number;
  /** Every message those people sent back. Absent on older responses. */
  reply_messages?: number;
  percent: number;
  /** Personalisation, live. All zero on an ordinary blast. Without these the
   *  only symptom of drafting falling behind is that a customised blast
   *  quietly was not customised. */
  personalized: boolean;
  drafts_written: number;
  drafts_fell_back: number;
  drafts_waiting: number;
  draft_cost_usd: number;
};

/** What the send pipe is doing, and what it has really been capable of.
 *  `sustained_per_minute` is measured from messages that actually left -
 *  nothing in the send path enforces a configured rate, so history is the
 *  only honest source for an estimate. */
export type Throughput = {
  pending: number;
  claimed: number;
  deferred: number;
  due_now: number;
  /** Past due but behind a paused campaign, so not going anywhere. Kept out
   *  of due_now: it is waiting work, not late work. */
  due_but_held: number;
  sent_last_hour: number;
  peak_per_minute: number;
  sustained_per_minute: number;
  measured_over_days: number;
};

/** Somebody who spoke last and has had no answer. */
export type AwaitingReply = {
  contact_id: number;
  name: string | null;
  phone_e164: string;
  at: string;
  body: string | null;
  hours_waiting: number;
  consent_status: string;
  looks_like_a_question: boolean;
};

/** What a trigger is and what it needs to be asked. Served by the API rather
 *  than hard-coded here, so a trigger the engine cannot actually fire never
 *  appears as a choice. */
export type TriggerSpec = {
  key: string;
  label: string;
  hint: string;
  live: boolean;
  params?: Array<{
    key: string;
    label: string;
    type: string;
    default: string | number;
  }>;
};

export type Campaign = {
  id: number;
  key: string;
  name: string;
  campaign_type: string;
  status: string;
  exclusivity_group?: string;
  /** Never texts anyone who has bought. On for the lead drips. */
  skip_customers?: boolean;
  active_enrollments: number;
  /** Held from this day on; earlier days and new people keep running. */
  hold_from_day?: number | null;
  hold_started_at?: string | null;
  /** people waiting on the hold */
  held_enrollments?: number;
  steps: Array<{
    id: number;
    step_index: number;
    day_offset: number;
    name?: string;
    delay_minutes: number;
    enabled: boolean;
  }>;
};

export type SendConfig = {
  kill_switch: boolean;
  kill_switch_reason?: string;
  rate_per_second: number;
  /** The real pace: texts a minute, across every send job together. */
  texts_per_minute: number;
  /** Overnight and weekend sign-ups' first texts: at most this many per 15
   *  minutes from the 11:00 opening. 0 = no limit. */
  backlog_per_15_min: number;
  daily_ceiling: number;
  sent_today: number;
  max_per_day: number;
  max_per_7d: number;
  min_gap_minutes: number;
  drafting_enabled: boolean;
  /** ISO weekdays sending runs on, 1=Mon..7=Sun. The one answer - it beats any
   *  per-campaign rule. null means every day. */
  sending_weekdays: number[] | null;
};

/** The sending speed: what it is set to, what actually went out, and when
 *  today's waiting texts finish at it (or at a previewed speed). */
export type Pace = {
  texts_per_minute: number;
  min: number;
  max: number;
  presets: Record<string, number>;
  paused: boolean;
  recent: Array<{ minute: string; sent: number }>;
  last_5_minutes_average: number;
  sends_today: boolean;
  window: { opens: string; closes: string };
  forecast_at: number;
  replies_per_100_texts: number;
  /** People replying an hour at forecast_at, from the last 3 days' ratio. */
  replies_an_hour: number;
  forecast: null | {
    texts: number;
    fits_today: boolean;
    finishes_at: string | null;
    left_at_close: number;
    by_hour: Array<{ hour: string; texts: number }>;
  };
};

/** A rule node. Deliberately loose - the backend is the authority on what
 *  compiles, and it returns a readable message when something does not. */
export type Rule = {
  op: string;
  field?: string;
  value?: unknown;
  min?: number;
  max?: number;
  children?: Rule[];
};

export type LiveStats = {
  contacts: number; reachable: number; sent_today: number; replies_today: number;
  opt_outs_today: number; opt_out_rate: number;
  /** People who replied over people texted, where the API is new enough. */
  reply_rate: number;
  in_campaigns: number; blasts_running: number;
  /** People, where replies_today is messages. Absent on older responses. */
  repliers_today?: number;
};

/** Replies counted in people and credited to the last text each person was
 *  sent. Every rate is a percentage already, so nothing multiplies by 100. */
export type PerformanceMetrics = {
  texts: number;
  people: number;
  replied_texts: number;
  repliers: number;
  reply_rate: number;
  /** the 95% range the true reply rate sits in - wide means "do not conclude" */
  reply_rate_low?: number;
  reply_rate_high?: number;
  /** false when there are too few replies to read anything into the rate */
  enough_data?: boolean;
  text_reply_rate: number;
  reply_messages: number;
  opt_outs: number;
  opt_out_rate: number;
  median_minutes_to_reply: number | null;
  repliers_within_1h: number;
  repliers_within_24h: number;
  /** answered calls credited to these texts (the last text before the call) */
  calls: number;
  called_people: number;
  calls_per_1000: number;
  calls_per_100_repliers: number | null;
};

export type PerformanceRow = PerformanceMetrics & {
  /** Opaque. Only good for telling rows apart. */
  key: Record<string, unknown>;
  label: string;
  detail?: string | null;
};

export type HistoryDay = {
  date: string; sent: number; delivered: number; replies: number; opt_outs: number;
};

/** One campaign's day. `replies` and `reply_rate` are people, not messages. */
export type DayOffsetRow = {
  campaign_id?: number; campaign?: string;
  day_offset: number; sent: number; people?: number;
  replies: number; reply_rate: number; opt_outs?: number; opt_out_rate?: number;
};

export type QueueItem = {
  contact_id: number; name: string; phone: string; last_reply_at?: string;
  body?: string; classification?: string; lead_score?: number;
  state?: string; flight_count: number;
};

export type User = {
  id: number; email: string; name?: string; role: string;
  team?: string; active: boolean; last_login_at?: string;
};


// ── background machinery: reminders, health, activity ───────
export type ReminderRow = {
  id: number;
  kind: string;
  kind_label: string;
  send_at: string;
  sent_at?: string | null;
  state: "sent" | "due" | "cancelled" | "overdue";
  why_cancelled?: string | null;
  appointment_at: string;
  appointment_id: string;
  calendar?: string | null;
  contact_id: number;
  name?: string | null;
  phone: string;
};

export type ReminderDay = {
  day: string;
  items: ReminderRow[];
  total: number;
  page: number;
  pages: number;
  summary: {
    sent: number;
    due: number;
    cancelled: number;
    overdue: number;
    appointments: number;
    cancelled_because: Record<string, number>;
  };
  calendars: string[];
};

export type Health = {
  at: string;
  sending: {
    in_sending_hours: boolean;
    paused: boolean;
    paused_because?: string | null;
    sent_today: number;
    sent_last_hour: number;
    sent_last_5_minutes: number;
    due_now: number;
    due_but_held: number;
    being_sent: number;
    deferred: number;
    oldest_due_minutes_late: number;
    failed_last_15_minutes: number;
    attempted_last_15_minutes: number;
    failures_today: Record<string, number>;
    held_back_today: Record<string, number>;
  };
  tokens: {
    auth_failures_last_hour: number;
    auth_failures_24h: number;
    gateway_unavailable_last_hour: number;
    throttled_last_hour: number;
  };
  webhooks: {
    replies_from_ghl: { last_at?: string | null; last_hour: number; last_24h: number };
    appointments: { last_at?: string | null; last_24h: number; stuck: number };
    purchases: { last_at?: string | null; last_24h: number; stuck: number };
  };
  reminders: {
    sent_today: number;
    still_due_today: number;
    cancelled_today: number;
    overdue: number;
    cancelled_because: Record<string, number>;
    next_due_at?: string | null;
  };
  backfill: {
    last_run_at?: string | null;
    calls_48h: number;
    contacts_received_48h: number;
    rows_failed_48h: number;
    newest_call_data?: string | null;
  };
  drafting: { switched_on: boolean; drafts_24h: Record<string, number> };
  database: { connections: number; max_connections: number; size: string };
  alerts: Array<{ key: string; severity: string; text: string }>;
};

export type ActivityPage = {
  kind: string;
  days: number;
  items: Array<Record<string, unknown>>;
  total: number;
  page: number;
  pages: number;
};

// ── redial engine ───────────────────────────────────────────────────────────
// Three switches, reported separately on purpose: "the engine is on" and "the engine
// is dialing" are different facts, and a screen that conflates them tells somebody
// people are being called when they are not.
/** Five9's own view. `error` non-null means we could NOT read it - which must never be shown as
 *  "every list is empty", the one reading that would make a dialling outage look like calm. */
export type RedialFive9State = {
  lists: {
    list: string; campaign: string; purpose: string; records: number; state: string;
    /** Records waiting with the campaign NOT running: those phones will not ring, and nothing
     *  else on any screen reports it. `Vaunt_Outbound` was found stopped on 2026-10-06.
     *  False when the state could not be read - a SOAP timeout is not a stopped campaign. */
    stalled: boolean;
    /** `getCampaignState` failed for this row. Unknown, which is not the same as stopped. */
    state_unreadable: boolean;
  }[];
  error: string | null;
  age_seconds: number;
};

export type RedialTraffic = {
  at: string;
  window_days: number;
  /** The range the server actually measured over, echoed back. The page prints THIS rather than
   *  recomputing "last 7 days" itself, so a heading cannot disagree with the rows under it. */
  window?: { from: string; to: string; days: number };
  /** The live `max_overdue_hours` setting. NULL means nothing is ever dropped for being stale. */
  overdue_hours: number | null;
  queue: {
    scheduled: number; due_now: number; waiting: number;
    /** Measured against `overdue_hours`, not a fixed 24 - it was `overdue_24h` until 2026-10-06. */
    overdue: number;
    in_flight: number;
    /** `in_flight` split: really in Five9 vs a worker holding the row having not called Five9 yet.
     *  Counted here rather than derived from `in_flight_detail`, which is `limit 50`. */
    in_five9: number; being_handed_over: number;
    people: number;
    /** The next dial STILL IN THE FUTURE. Null when everything scheduled is already late. */
    next_at: string | null;
    /** The oldest attempt already past its time - "next" must never be a date in the past. */
    oldest_due_at: string | null;
  };
  queue_by_entry: { entry_type: string; n: number; due_now: number }[];
  /** Per Eastern hour, both halves of the day: handed over, actually rang, and still to come. */
  schedule_today: { hr: number; to_go: number; went_out: number; rang: number }[];
  /** Dials handed over OUTSIDE the window their own policy allows - the 04:50 incident, as a
   *  number. Non-empty is an alarm; the page must raise it, not leave it to be spotted in a row.
   *  One row per (hour, policy): a window averaged across policies is one the breach may not have
   *  breached. */
  out_of_window_today: {
    hr: number; n: number; people: number; policy_key: string;
    window_open: string; window_close: string;
  }[];
  /** The window governing the MOST queued dial attempts, plus how many sit under a different one.
   *  Not "the window all policies agree on" - they never do, because the test cadence is
   *  deliberately 00:00-23:59, and that rule left the page unable to name a window at all. */
  dial_window: {
    window_open: string; window_close: string; n: number; others: number;
  }[];
  /** Five9 did not confirm the removal, so the record may still be live. ALL TIME on purpose: a
   *  date filter made this alarm clear itself at midnight while the person was still in a list. */
  removal_unverified: { n: number; people: number; oldest_at: string | null }[];
  /** Who wrote to the Five9 lists, from `list_ledger.actor_system` - the only source that sees
   *  TextersAI and the appointment automation, whose adds never create a plan of ours. */
  writers_five9: {
    actor_system: string; adds: number; removes: number; people_added: number;
    lists_touched: number; last_at: string | null;
  }[];
  dials_by_day: {
    day: string; handed_over: number; confirmed: number; people: number;
    continued: number; ended: number;
  }[];
  schedule_horizon: { bucket: string; n: number; first_at: string; last_at: string }[];
  queue_by_list: { list_name: string; n: number }[];
  in_flight_detail: {
    /** The attempt id. A (phone, dial_no) key collides: one person can hold two plans in two
     *  lists at the same step, which is a state this estate really produces. */
    id: number;
    phone_last10: string; list_name: string; dial_no: number;
    added_at: string | null; add_code: number | null; entry_type: string;
    /** 'added' = really in Five9. 'claimed' = a worker locked the row and has NOT called Five9
     *  yet, so `added_at` is null and the person is not in the dialler. */
    status: string;
  }[];
  writers_today: { reason: string; created_by: string; plans: number }[];
  writers_window: { reason: string; created_by: string; plans: number; still_active: number }[];
  list_today: { operation: string; actor_system: string; n: number; records_deleted: number }[];
  list_by_day: { day: string; adds: number; removes: number; sweep_removes: number }[];
  dials_today: { confirmed_dials: number; handed_to_five9: number; people: number }[];
  cadence_funnel: {
    dial_no: number; attempts: number; dialled: number; continued: number; ended: number;
  }[];
  /** `kind` separates a real HOLD from a call that simply finished (`last_error` is never cleared,
   *  so a completed call ends with a null reason) and from one still waiting its turn. Showing all
   *  three as holds put successful calls in an amber row titled "why we did not call". */
  holds_today: { reason: string; status: string; n: number; kind: string }[];
  holds_window: { reason: string; status: string; n: number; kind: string }[];
  settings: {
    enabled: boolean; dry_run: boolean; dialer_enabled: boolean;
    daily_cap: number | null; max_in_flight_redials: number | null;
    updated_by: string | null; updated_at: string | null;
    /** The heartbeat. `enabled` is a flag in a table; these say whether it is RUNNING.
     *  `last_dial_at` is separate on purpose - a tick that correctly dials nobody out of hours is
     *  healthy, so `last_tick_at` alone cannot tell "working" from "working and idle". */
    last_tick_at: string | null;
    last_tick_summary: string | null;
    last_dial_at: string | null;
    seconds_since_tick: number | null;
  };
  policies: {
    key: string; entry_type: string; window_open: string; window_close: string;
    redials_per_day: number | null; enabled: boolean;
  }[];
  cadence_rules: {
    priority: number; entry_type: string | null; sms_campaign: string | null;
    day_min: number | null; day_max: number | null; policy_key: string;
    enabled: boolean; note: string | null;
  }[];
};

export type RedialStatus = {
  at: string;
  engine_enabled: boolean;
  dry_run: boolean;
  five9_dialer_enabled: boolean;
  five9_credentials_present: boolean;
  list_name: string;
  /** null means NO CAP. */
  daily_cap: number | null;
  dialed_today: number;
  /** Dials this engine is going to place today. The headline number. */
  /** Adds Five9 accepted. NOT the number of calls made. */
  added_to_list_today: number;
  /** Calls that actually happened - a matched Five9 call record. */
  dialled_and_confirmed_today: number;
  still_to_be_dialled: number;
  waiting_with_us: number;
  handed_to_five9_awaiting_dial: number;
  handed_to_five9_today: number;
  people_still_coming: number;
  next_dial_at: string | null;
  /** `last_dial_at` was removed here on 2026-10-06: it was an alias for the FURTHEST SCHEDULED
   *  attempt, so it answered "when did we last dial" with a date months in the future. */
  furthest_scheduled_at: string | null;
  last_confirmed_dial_at: string | null;
  /** The heartbeat. `engine_enabled` is a flag; these say whether it is really running. */
  engine_last_tick_at: string | null;
  engine_seconds_since_tick: number | null;
  engine_last_tick_summary: string | null;
  engine_last_dial_at: string | null;
  plans: Record<string, number>;
  attempts: Record<string, number>;
  due_now: number;
  overdue_15m: number;
  /** Five9 did not confirm the removal, so these people may still be being dialed. */
  removal_unverified: number;
  /** "database" or "env (...)" — which source the switches came from. Answers "I changed
   *  it and nothing happened". */
  config_source: string;
};

export type RedialSettings = {
  enabled: boolean;
  dry_run: boolean;
  dialer_enabled: boolean;
  daily_cap: number;
  updated_by: string | null;
  updated_at: string | null;
};

export type RedialBlock = {
  phone_last10: string;
  reason: string;
  blocked_by: string | null;
  created_at: string;
  expires_at: string | null;
};

export type RedialPolicy = {
  key: string;
  name: string;
  description: string | null;
  entry_type: string;
  reply_day_offsets: number[];
  send_texts: boolean;
  redial_daily: boolean;
  redial_at_local: string;
  max_days: number;
  min_gap_seconds: number;
  window_open: string;
  window_close: string;
  enabled: boolean;
};

export type RedialRule = {
  id: number;
  source: "five9" | "tracker";
  disposition: string;
  action: string;
  precedence: number;
  note: string | null;
  enabled: boolean;
};

export type RedialAttempt = {
  dial_no: number;
  phase: string;
  scheduled_for: string;
  rolled: boolean;
  status: string;
  add_code: number | null;
  added_at: string | null;
  removed_at: string | null;
  records_deleted: number | null;
  five9_disposition: string | null;
  tracker_disposition: string | null;
  decided_action: string | null;
  decided_reason: string | null;
  last_error: string | null;
};

export type RedialPlan = {
  id: number;
  phone_last10: string;
  contact_id: number | null;
  policy_key: string;
  entry_type: string;
  reason: string | null;
  reason_ref: string | null;
  list_name: string;
  routing_target: string | null;
  entered_at: string;
  phase: string;
  dials_done: number;
  next_action_at: string | null;
  status: string;
  stop_reason: string | null;
  attempts: RedialAttempt[];
};


// ── dialing analytics ───────────────────────────────────────────────────────
// Two halves that must not be added together: `overview` is every Five9 dial whoever placed
// it, `routing`/`lists`/`engine` are only the dials THIS engine placed. `attribution` says
// which dimensions are unavailable and why, so a column can be greyed out instead of showing
// a zero that reads as "nothing happened".
export type DialingOverview = {
  from: string;
  to: string;
  dials: number;
  people: number;
  reached_a_human: number;
  /** over CLASSIFIED dials, not all of them - an unmapped disposition would drag it down.
   *  OUTCOME-BUCKET based; see `reached_basis`. */
  reached_rate: number | null;
  classified: number;
  unclassified: number;
  inbound: number;
  transfers: number;
  talk_minutes: number;
  avg_talk_seconds: number | null;
  dials_with_an_agent: number;
  /** The DURATION definition of connected, over the same dials as every other field here.
   *  The headline tiles read this instead of the breakdown's totals, which carry the Explore
   *  panel's filters and would put two denominators in one row. */
  connected_seconds?: number;
  connected_at_threshold?: number;
  connected_at_threshold_rate?: number | null;
  /** Dials that cannot be tested against ANY duration threshold. A duration of unknown is not
   *  a duration of zero, which is why "connected at any length" is not the same number as
   *  `dials` - 3,664 of 19,121 on 2026-10-07. */
  duration_unknown?: number;
  by_day: { day: string; dials: number; reached_a_human: number; inbound: number }[];
};

export type DialingDisposition = {
  disposition: string; outcome: string; dials: number; with_agent: number;
  reached_a_human: number; avg_seconds: number | null; longest_seconds: number | null;
};

export type DialingAgent = {
  /** Canonical name, matching every other dialing table. `calls` is the deprecated alias
   *  this one endpoint used alone, which made cross-table sums silently miss it. */
  dials?: number;
  agent: string; calls: number; people: number; talk_minutes: number | null;
  avg_seconds: number | null; appointments: number; sales: number; transfers: number;
  not_interested: number; unqualified: number;
};

export type DialingRouting = {
  routed_to: string; dials: number; people: number; reached_a_human: number;
  answered_by_an_agent: number; avg_seconds: number | null; appointments: number;
  sales: number; reached_rate: number | null;
};

export type DialingList = {
  list_name: string; adds: number; adds_accepted: number; already_present: number;
  removal_unverified: number; dials_matched: number; reached_a_human: number;
};

export type DialingEngine = {
  from: string; to: string;
  entries: { entry_type: string; reason: string; plans: number; active: number;
             paused: number; stopped: number; completed: number }[];
  stops: { stop_reason: string; plans: number }[];
  attempts: Record<string, number>;
  redials_of_leads_added_the_same_day: number;
  redials_of_leads_added_earlier: number;
};

/** The LIVE queue - what is about to be dialed. Not date-ranged. */
export type DialingQueue = {
  at: string;
  /** Still coming: ours to send PLUS those already sitting in the Five9 list. */
  still_to_be_dialled: number;
  waiting_with_us: number;
  /** In the Five9 list, NOT dialled - Five9's dialer is gated on agent availability. */
  handed_to_five9_awaiting_dial: number;
  being_sent: number;
  people_still_coming: number;
  /** Calls that ACTUALLY happened - a matched Five9 call record. */
  dialled_and_confirmed_today: number;
  handed_to_five9_today: number;
  dialled_and_confirmed_ever: number;
  removal_unverified: number;
  failed: number;
  next_dial_at: string | null;
  last_dial_at: string | null;
  by_hour: { hour: string; dials: number }[];
  by_list: { list_name: string; dials: number; people: number }[];
  active_plans: number;
  paused_plans: number;
  people_in_cadence: number;
  engine_live: boolean;
  daily_cap: number | null;
  note: string;
};

export type DialingEverything = {
  queue: DialingQueue;
  overview: DialingOverview;
  dispositions: DialingDisposition[];
  agents: DialingAgent[];
  routing: { routing: DialingRouting[]; note: string };
  lists: { lists: DialingList[]; note: string };
  engine: DialingEngine;
  list_operations: {
    operations: { actor_system: string; list_name: string; operation: string;
                  operations: number; confirmed: number; failed: number;
                  unverifiable: number }[];
    systems_reporting: string[];
    available: boolean;
    note: string;
  };
  attribution: {
    dials: number;
    resolved_to_a_person_pct: number;
    /** Dials we could not tie to a PERSON. Distinct from
     *  `agents_not_in_a_vaunt_group`, which counts dials whose AGENT has no role -
     *  two different "unclassified" numbers in one payload. */
    dials_not_resolved_to_a_person: number;
    /** @deprecated read `dials_not_resolved_to_a_person`. */
    unresolved: number;
    with_an_agent_pct: number;
    direction_unknown_pct: number;
    from_the_webhook: number;
    from_bigquery: number;
    /** The THIRD ingest. It existed for a week while this panel named only two, so the two it
     *  named added up to 13,572 against a headline of 16,557 dials. */
    from_the_call_log?: number;
    /** Every feed, so the list always adds up to `dials` whatever gets wired next. */
    by_feed?: { source: string; dials: number; with_a_campaign: number }[];
    /** Campaign coverage per day. A day in `days_with_no_campaign_at_all` is a day the
     *  call-log ingest produced nothing - campaign is unavailable for it, which is a different
     *  fact from the campaigns being unnamed. */
    campaign?: {
      dials: number;
      with_a_campaign: number;
      without_a_campaign: number;
      known_pct: number | null;
      by_day: { day: string; dials: number; with_a_campaign: number }[];
      days_with_no_campaign_at_all: string[];
      note: string;
    };
    /** What the Vaunt scope threw away, by name. A Vaunt campaign appearing here means the
     *  scope is too narrow and this page is understating the dialling. */
    other_clients_excluded?: {
      dials: number;
      campaigns: { campaign: string; dials: number }[];
      note: string;
    };
    unavailable: Record<string, string>;
    /** Agents taking calls who are in neither Five9 Vaunt agent group, so have no role.
     *  Shown rather than hidden: "we cannot classify 141 of Paulo's dials" is a different
     *  fact from "Paulo made no calls". */
    agents_not_in_a_vaunt_group?: {
      available: boolean;
      dials: number;
      agents: { agent: string; dials: number }[];
      note: string;
    };
  };
  by_routing_compared: DialingBreakdown;
  by_entry_type: DialingBreakdown;
  connected_seconds: number;
};

export type DialingCell = {
  dials: number;
  people: number;
  connected: number;
  answered: number;
  connect_rate: number | null;
  answer_rate: number | null;
  talk_minutes: number;
  avg_seconds: number | null;
  appointments: number;
  sales: number;
  transfers: number;
  not_interested: number;
  inbound: number;
  unclassified: number;
  /** The grouped dimensions arrive under their own names, e.g. `routing`, `day`. */
  [dimension: string]: string | number | null;
};

export type DialingBreakdown = {
  from: string;
  to: string;
  group_by: string[];
  filters: Record<string, string>;
  connected_seconds: number;
  rows: DialingCell[];
  totals: { dials: number; connected: number; answered: number };
  dimensions_available: string[];
  dimensions_only_for_our_dials: string[];
  comparing?: string;
  /** What the query actually ran with - NOT what the page asked for. Omit `ours_only` on the
   *  way out and the server decides; this is how the checkbox gets shown in the state the
   *  numbers were computed in. */
  ours_only?: boolean;
  ours_only_was_chosen_for_you?: boolean;
  /** Dials inside the range that are NOT in this table, and why. Excluding rows is fine;
   *  excluding them silently is the bug. `dials_excluded + totals.dials` equals
   *  `dials_in_range_before_excluding`, which equals /dialing/overview for the same range. */
  dials_excluded?: number;
  dials_in_range_before_excluding?: number;
  excluded_because?: string | null;
  /** Buckets the query found vs rows returned. `truncated` means the tail is not listed -
   *  the totals are still complete, being computed unlimited. */
  groups_total?: number;
  rows_shown?: number;
  truncated?: boolean;
  not_ours_label?: string;
  /** Set when a cadence dimension is grouped with `ours_only` explicitly off, explaining why
   *  one row holds nearly everything. Null when it does not apply. */
  cadence_dimensions_are_empty_because?: string | null;
  note: string;
};

/** The values a filter on one dimension can actually match, in the chosen range. Fetched so
 *  the filter is a choice rather than a spelling test - `closers` matched 2,088 dials and
 *  `Closers` matched none, and both look identical on screen. */
export type DialingValues = {
  dimension: string;
  from: string;
  to: string;
  values: { value: string; dials: number }[];
  truncated: boolean;
  total_values: number;
  note: string;
};

export type DialingDimensions = {
  dimensions: string[];
  only_for_our_dials: string[];
  default_connected_seconds: number;
  not_ours_label?: string;
  note: string;
};

export const api = {
  /** The appointment reminder schedule for one Eastern day. */
  reminders: (p: { day?: string; state?: string; calendar?: string; q?: string; page?: number; per_page?: number }) =>
    get<ReminderDay>(
      "/api/v1/reminders?" +
        new URLSearchParams(
          Object.entries(p)
            .filter(([, v]) => v !== undefined && v !== "")
            .map(([k, v]) => [k, String(v)]),
        ).toString(),
    ),
  contactReminders: (id: number) =>
    get<{ items: ReminderRow[]; total: number }>(`/api/v1/contacts/${id}/reminders`),
  /** Everything running in the background, cached a minute on the server. */
  health: () => get<Health>("/api/v1/ops/health"),

  /** Every section in ONE request, so the whole page reflects one moment. Eight separate
   *  calls would show sections from slightly different instants and invite somebody to
   *  reconcile numbers that were never meant to add up. */
  dialing: (range: { since?: string; until?: string; connected_seconds?: number }) =>
    get<DialingEverything>(
      "/api/v1/dialing/everything?" +
        new URLSearchParams(
          Object.entries(range)
            .filter(([, v]) => v !== undefined && v !== "")
            .map(([k, v]) => [k, String(v)]),
        ),
    ),

  /** The pivot. One or two dimensions, optional filters, and the threshold that decides
   *  what "connected" means - all three are the reader's choice, because "basic reporting"
   *  was the complaint the page was rebuilt to answer. */
  dialingBreakdown: (p: {
    since?: string;
    until?: string;
    group_by: string[];
    filters?: Record<string, string>;
    connected_seconds?: number;
    /** THREE-STATE. `undefined` means "server, you decide" - it turns itself on when a cadence
     *  dimension is grouped, which is the only way to keep the '(another system placed it)'
     *  bucket off the page without hiding dials. `false` is an explicit "include everything"
     *  and must be sent, so this is not a `if (p.ours_only)` guard. */
    ours_only?: boolean;
  }) => {
    const q = new URLSearchParams();
    if (p.since) q.append("since", p.since);
    if (p.until) q.append("until", p.until);
    p.group_by.forEach((g) => q.append("group_by", g));
    // Parallel arrays, matching the endpoint: the dimension whitelist stays the only thing
    // that can reach the SQL, so a filter cannot be smuggled in as a query parameter name.
    Object.entries(p.filters ?? {}).forEach(([k, v]) => {
      if (v) {
        q.append("filter_key", k);
        q.append("filter_value", v);
      }
    });
    if (p.connected_seconds != null) q.append("connected_seconds", String(p.connected_seconds));
    // `!= null`, not truthiness: `false` has to travel. It used to be `if (p.ours_only)`, which
    // made "I unticked the box" indistinguishable from "I never touched it" - and once the
    // server started defaulting the switch on, untickable.
    if (p.ours_only != null) q.append("ours_only", String(p.ours_only));
    return get<DialingBreakdown>("/api/v1/dialing/breakdown?" + q);
  },

  /** The values one dimension actually takes in a range, for the filter dropdown. */
  dialingValues: (p: { dimension: string; since?: string; until?: string }) => {
    const q = new URLSearchParams({ dimension: p.dimension });
    if (p.since) q.append("since", p.since);
    if (p.until) q.append("until", p.until);
    return get<DialingValues>("/api/v1/dialing/values?" + q);
  },

  /** What is about to be dialed. No date range - it is the live queue. */
  dialingQueue: () => get<DialingQueue>("/api/v1/dialing/queue"),

  /** What you may group by and filter on. Fetched rather than hard-coded here, so the
   *  dropdowns cannot drift from the whitelist the backend actually enforces. */
  dialingDimensions: () => get<DialingDimensions>("/api/v1/dialing/dimensions"),

  //: The engine's live operational picture. Added 2026-10-06 after a 04:50 ET tick put 38 real
  //: leads in a dialing list six hours early and the ONLY place it was visible was a tick's JSON
  //: response - no screen would have shown it.
  /** What FIVE9 says, not what we think: record counts per list and whether its campaign runs.
   *  Six SOAP round trips behind a 90s server-side cache, so poll it on its own slower timer -
   *  not with the 20-second refresh the Postgres-backed traffic snapshot uses. */
  redialFive9State: () => get<RedialFive9State>("/api/v1/redial/five9-state"),
  /** Either a preset window or two inclusive Eastern days. Explicit dates win on the server. */
  redialTraffic: (r: { days?: number; from?: string; to?: string } = {}) => {
    const p =
      r.from && r.to
        ? `from_day=${encodeURIComponent(r.from)}&to_day=${encodeURIComponent(r.to)}`
        : `days=${r.days ?? 7}`;
    return get<RedialTraffic>(`/api/v1/redial/traffic?${p}`);
  },
  redialStatus: () => get<RedialStatus>("/api/v1/redial/status"),
  redialPolicies: () =>
    get<{ count: number; policies: RedialPolicy[]; window_timezone: string }>(
      "/api/v1/redial/policies",
    ),
  redialRules: () =>
    get<{ count: number; rules: RedialRule[]; precedence: string }>(
      "/api/v1/redial/rules",
    ),
  /** The switches. `confirm: "LIVE"` is required only when the change starts real calls. */
  redialSetSettings: (body: {
    enabled?: boolean;
    dry_run?: boolean;
    dialer_enabled?: boolean;
    daily_cap?: number;
    confirm?: string;
  }) =>
    patch<{ changed: boolean; settings: RedialSettings; now_dialing?: boolean }>(
      "/api/v1/redial/settings",
      body,
    ),
  redialSetRule: (body: {
    source: "five9" | "tracker";
    disposition: string;
    action?: string;
    precedence?: number;
    enabled?: boolean;
    note?: string;
  }) => patch<{ changed?: boolean; summary?: string; nothing_changed?: boolean }>(
    "/api/v1/redial/rules",
    body,
  ),
  redialSetPolicy: (body: {
    key: string;
    send_texts?: boolean;
    redial_daily?: boolean;
    max_days?: number;
    min_gap_seconds?: number;
    spread_redials?: boolean;
    enabled?: boolean;
  }) => patch<{ changed?: boolean; summary?: string; nothing_changed?: boolean }>(
    "/api/v1/redial/policies",
    body,
  ),
  redialBlocks: (includeExpired = false) =>
    get<{ count: number; blocks: RedialBlock[] }>(
      `/api/v1/redial/blocks?include_expired=${includeExpired ? "true" : "false"}`,
    ),
  redialBlockLead: (body: { phone: string; reason: string; hours?: number }) =>
    post<{ blocked: boolean; phone_last10: string; plans_stopped?: number }>(
      "/api/v1/redial/blocks",
      body,
    ),
  redialUnblockLead: (phone: string) =>
    del(`/api/v1/redial/blocks/${encodeURIComponent(phone)}`),
  redialStopPlans: (body: { phone?: string; reason_ref?: string; reason?: string }) =>
    post<{ stopped: number }>("/api/v1/redial/plans/stop", body),
  redialLead: (q: { phone?: string; contact_id?: number }) =>
    get<{ plans: RedialPlan[]; count: number }>(
      "/api/v1/redial/lead?" +
        new URLSearchParams(
          Object.entries(q).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)]),
        ),
    ),
  activity: (kind: string, days = 7, page = 1, perPage = 50) =>
    get<ActivityPage>(`/api/v1/ops/activity?kind=${kind}&days=${days}&page=${page}&per_page=${perPage}`),
  me: () => get<Me>("/auth/me"),
  logout: () => post("/auth/logout"),

  fields: () => get<FieldOption[]>("/api/v1/fields"),
  fieldValues: (field: string) =>
    get<Array<{ value: string; label: string; count: number | null }>>(
      `/api/v1/field-values?field=${encodeURIComponent(field)}`,
    ),

  customFields: () =>
    get<
      Array<{
        id: number;
        key: string;
        label: string;
        data_type: string;
        options: string[];
        is_segmentable: boolean;
        source: string;
        archived: boolean;
        in_use: number;
      }>
    >("/api/v1/custom-fields"),
  createCustomField: (f: {
    label: string;
    data_type: string;
    options?: string[];
  }) => post<{ id: number; key: string; restored: boolean }>("/api/v1/custom-fields", f),
  updateCustomField: (id: number, patchBody: Record<string, unknown>) =>
    patch(`/api/v1/custom-fields/${id}`, patchBody),
  deleteCustomField: (id: number, purge = false) =>
    request<{ archived: boolean; purged: boolean; values_erased: number }>(
      `/api/v1/custom-fields/${id}?purge=${purge}`,
      { method: "DELETE" },
    ),
  previewAudience: (rule: Rule) => post<RulePreview>("/api/v1/audience/preview", rule),

  grantConsent: (rule: Rule, source: string, evidence?: string) =>
    post<{
      matched: number;
      granted: number;
      already: number;
      refused_opted_out: number;
    }>("/api/v1/audience/consent", { rule, source, evidence }),

  audienceImpact: (
    rule: Rule,
    limit?: number | null,
    exclusions?: Record<string, boolean>,
  ) =>
    post<{
      reachable: number;
      after_exclusions: number;
      removed: number;
      available_exclusions: Array<{ key: string; label: string }>;
      in_campaign: { count: number; percent: number; by_campaign: Array<{ name: string; count: number }> };
      already_queued: { count: number; percent: number };
      messaged_24h: { count: number; percent: number };
      in_another_blast: { count: number; percent: number };
      never_messaged: { count: number; percent: number };
      timezones: Array<{ zone: string; count: number }>;
    }>("/api/v1/audience/impact", { rule, limit, exclusions }),

  traffic: () =>
    get<{
      /** what the autopilot will actually release from what is scheduled below */
      autopilot: {
        on: boolean;
        live: boolean;
        mode?: "room" | "metering" | "catch_up" | "off_shift";
        conversations_per_hour?: number;
        texts_per_hour?: number | null;
        agents?: number;
        waiting?: number | null;
        reason?: string;
      };
      contacts: number;
      contactable: number;
      blocked: Record<string, number>;
      sent_today: number;
      queued_today: number;
      queued_total: number;
      will_receive_today: number;
      daily_ceiling: number;
      ceiling_used: number;
      kill_switch: boolean;
      by_origin: Array<{ origin: string; count: number }>;
      upcoming_hours: Array<{ hour: string; count: number }>;
      active_campaigns: number;
      paused_campaigns: number;
      running_blasts: number;
      suppressed: number;
      /** People who get at least one text today (sent or still to go). */
      people_today: number;
      /** Today as one calculation, shared with the Settings speed card. */
      today: {
        sending_day: boolean;
        per_minute: number;
        texts_sent: number;
        texts_to_go: number;
        texts_total: number;
        /** everyone who gets at least one text today, sent or still to go */
        people: number;
        /** of them, those texted already */
        people_sent: number;
        fits_today: boolean;
        finishes_at: string | null;
        left_at_close: number;
        by_hour: Array<{ hour: string; sent: number; planned: number }>;
        by_campaign: Array<{ name: string; sent: number; to_go: number }>;
      };
      not_today: { total: number; reasons: Array<{ key: string; label: string; count: number }> };
    }>("/api/v1/stats/traffic"),

  /** What each hour of today is made of, and how many replies to expect.
   *  Its own call because the reply rates come from 21 days of history; the
   *  server caches them for half an hour. */
  hourMix: () =>
    get<{
      day: string;
      sending_day: boolean;
      hours: Array<{
        hour: string;
        sent: number;
        planned: number;
        total: number;
        people: number;
        never_replied: number;
        never_replied_pct: number;
        expected_replies: number;
        expected_rate_pct: number;
        /** how much of the expectation is borrowed rather than measured */
        estimated_share_pct: number;
        /** people whose FIRST text of the day lands in this hour */
        first_touched: number;
        mix: Array<{ label: string; n: number; pct: number }>;
        flags: string[];
      }>;
      totals: { texts: number; expected_replies: number; never_replied_pct: number };
      /** what actually happened on recent comparable days, so the model is
       *  never read without a yardstick beside it */
      recent_actual_repliers: Array<{ day: string; repliers: number }>;
      rates: Array<{ family: string; bucket: string; rate_pct: number; sends: number }>;
      as_of: string;
    }>("/api/v1/stats/hour-mix"),

  cohorts: (by: string, days = 90) =>
    get<{
      by: string;
      label: string;
      at_send: boolean;
      days: number;
      dimensions: Array<{ key: string; label: string; at_send: boolean }>;
      rows: Array<{
        cohort: string;
        people: number;
        sent: number;
        delivered: number;
        failed: number;
        repliers: number;
        opted_out: number;
        reply_rate: number;
        opt_out_rate: number;
      }>;
    }>(`/api/v1/stats/cohorts?by=${by}&days=${days}`),

  performance: (params: {
    group_by?: string;
    days?: number;
    /** YYYY-MM-DD, Eastern, inclusive. Used instead of `days`. */
    from?: string;
    to?: string;
    campaign_id?: number;
    blast_id?: number;
    kind?: "campaign" | "blast" | "reminder";
  }) => {
    const qs = new URLSearchParams(
      Object.entries(params)
        .filter(([, v]) => v != null && v !== "")
        .map(([k, v]) => [k, String(v)]),
    );
    return get<{
      group_by: string;
      from: string;
      to: string;
      timezone: string;
      dimensions: Array<{ key: string; label: string }>;
      totals: PerformanceMetrics;
      rows: PerformanceRow[];
    }>(`/api/v1/stats/performance?${qs}`);
  },

  suppressContact: (id: number) =>
    post<{ ok: boolean }>(`/api/v1/contacts/${id}/suppress`, {}),
  checkSuppression: (phone: string) =>
    get<{ phone: string; blocked: boolean; reason?: string; source?: string }>(
      `/api/v1/suppressions/check?phone=${encodeURIComponent(phone)}`,
    ),

  segments: () => get<Segment[]>("/api/v1/segments"),
  createSegment: (s: { key: string; name: string; definition: Rule }) =>
    post<Segment>("/api/v1/segments", s),

  contacts: (params: {
    q?: string;
    segment_id?: number;
    page?: number;
    per_page?: number;
    sort?: string;
    desc?: boolean;
  }) => {
    const qs = new URLSearchParams(
      Object.entries(params)
        .filter(([, v]) => v != null && v !== "")
        .map(([k, v]) => [k, String(v)]),
    );
    return get<{
      items: Contact[];
      total: number;
      page: number;
      pages: number;
      per_page: number;
    }>(`/api/v1/contacts?${qs}`);
  },
  contact: (id: number) => get<Contact>(`/api/v1/contacts/${id}`),
  timeline: (id: number) => get<TimelineEntry[]>(`/api/v1/contacts/${id}/timeline`),

  templates: () => get<Template[]>("/api/v1/templates"),
  cost: (body: string, recipients: number) =>
    post<CostPreview>("/api/v1/templates/cost", { body, recipients }),

  blasts: () => get<Blast[]>("/api/v1/blasts"),
  createBlast: (b: Record<string, unknown>) => post<Blast>("/api/v1/blasts", b),
  /** Write a few real drafts for real people, so somebody can read them
   *  before pointing this at fifty thousand contacts. A window, not a gate:
   *  nothing waits on it and nothing is stored. */
  personalizedPreview: (rule: Record<string, unknown>, brief: string, count = 4) =>
    post<{ drafts: PersonalizedDraft[] }>("/api/v1/blasts/personalized-preview", {
      rule,
      brief,
      count,
    }),
  blastProgress: (id: number) => get<BlastProgress>(`/api/v1/blasts/${id}/progress`),

  /** Live queue depth plus measured capacity. The blast screen uses
   *  `sustained_per_minute` for its estimate rather than the rate someone
   *  typed, because asking for 600 a minute does not produce 600 a minute. */
  throughput: () => get<Throughput>("/api/v1/sending/throughput"),

  /** People whose last message is theirs and nobody has written back. */
  awaitingReply: (days = 30, limit = 100) =>
    get<AwaitingReply[]>(
      `/api/v1/replies/awaiting?days=${days}&limit=${limit}`,
    ),
  /** `armed` when the blast builds its list at send time: nothing was queued,
   *  and the worker builds and sends at `sends_at`. */
  launchBlast: (id: number) =>
    post<{ queued: number; armed?: boolean; sends_at?: string | null }>(
      `/api/v1/blasts/${id}/launch`,
    ),
  pauseBlast: (id: number) => post<{ held: number }>(`/api/v1/blasts/${id}/pause`),
  /** Releases the rows pause held back. The endpoint has always existed; the
   *  console never called it, so a paused blast could only be stopped - the
   *  rest of its audience was unreachable without a hand-written query. */
  resumeBlast: (id: number) => post<{ released: number }>(`/api/v1/blasts/${id}/resume`),
  cancelBlast: (id: number, reason?: string) =>
    post<{ already_sent: number; canceled: number }>(`/api/v1/blasts/${id}/cancel`, { reason }),

  campaigns: () => get<Campaign[]>("/api/v1/campaigns"),
  createCampaign: (c: {
    name: string;
    campaign_type: string;
    description?: string;
    /** "immediate" or "at_time" — what to do when someone enters after Day 0's
     *  hour has already passed. */
    first_message_mode?: string;
    steps: Array<{
      name?: string;
      day_offset: number;
      delay_minutes: number;
      delay_from?: string;
      /** Set on the first step of each day: the clock time that day opens at. */
      at_time?: string;
      template_id: number | null;
      body?: string;
      send_window_start?: string;
      send_window_end?: string;
    }>;
  }) => post<Campaign>("/api/v1/campaigns", c),
  publishCampaign: (id: number) =>
    post<Campaign>(`/api/v1/campaigns/${id}/publish`, {}),
  enrollAudience: (
    id: number,
    rule: Rule,
    exclusions?: Record<string, boolean>,
    limit?: number | null,
    ratePerMinute?: number,
    confirmDuplicate?: boolean,
  ) =>
    // Up to 1,000 people are enrolled at once and the counts are final; more
    // are enrolled in the background (status "queued") - the Campaigns page
    // shows the progress.
    post<EnrolReply>(
      `/api/v1/campaigns/${id}/enroll-audience`, {
      rule,
      exclusions,
      limit,
      // Spreads when each person becomes due. A campaign has no rate control
      // of its own, so without this the send rate is whatever the worker's
      // batch size happens to be.
      rate_per_minute: ratePerMinute,
      confirm_duplicate: confirmDuplicate || undefined,
      }),
  /** Was this exact audience started on a campaign in the last few hours? */
  sameAudience: (rule: Rule, exclusions?: Record<string, boolean>, limit?: number | null) =>
    post<{ same: boolean; message: string | null; earlier: EnrollmentJob[] }>(
      "/api/v1/audience/same-check", { rule, exclusions, limit }),
  /** where the people in a campaign are sitting right now, by its day */
  campaignPositions: (id: number) =>
    get<{
      campaign_id: number;
      active: number;
      exited: number;
      finished: number;
      days: Array<{
        day_offset: number;
        step_index: number;
        name: string | null;
        people: number;
        next_text: string | null;
        due_today: number;
      }>;
    }>(`/api/v1/campaigns/${id}/positions`),
  enrollmentJobs: () => get<{ jobs: EnrollmentJob[] }>("/api/v1/enrollment-jobs"),
  cancelEnrollment: (id: number) => post<EnrollmentJob>(`/api/v1/enrollment-jobs/${id}/cancel`, {}),
  pauseCampaign: (id: number, reason?: string) =>
    post<{ status: string; enrollments_held: number }>(
      `/api/v1/campaigns/${id}/pause`,
      { reason },
    ),
  campaignSettings: (id: number, body: { skip_customers: boolean }) =>
    patch<{ id: number; skip_customers: boolean }>(`/api/v1/campaigns/${id}/settings`, body),
  resumeCampaign: (id: number) =>
    post<{ status: string; due_immediately: number }>(
      `/api/v1/campaigns/${id}/resume`,
      {},
    ),
  holdCampaign: (id: number, fromDay: number) =>
    post<{ hold_from_day: number; paused_now: number; held: number }>(
      `/api/v1/campaigns/${id}/hold`,
      { from_day: fromDay },
    ),
  releaseCampaign: (id: number, shift: boolean) =>
    post<{ resumed: number; due_immediately: number; shifted: boolean }>(
      `/api/v1/campaigns/${id}/release`,
      { shift },
    ),
  campaignDetail: (id: number) =>
    get<{
      id: number;
      name: string;
      campaign_type: string;
      status: string;
      version_no: number;
      version_status: string | null;
      active_enrollments: number;
      first_message_mode: string;
      steps: Array<{
        step_index: number;
        day_offset: number;
        /** "day_time" opens the day at at_time; "previous" follows the step
         *  before it by delay_minutes. */
        delay_from: string;
        at_time: string | null;
        delay_minutes: number;
        send_window_start: string;
        template_id: number | null;
        body: string | null;
        personalize: boolean;
        personalize_brief: string | null;
      }>;
      transitions: Array<{
        id: number;
        trigger: string;
        trigger_params: Record<string, string | number> | null;
        condition: Record<string, unknown> | null;
        to_campaign_id: number | null;
        delay_minutes: number;
        start_at_step_index: number;
        add_tags: string[];
        remove_tags: string[];
        exit_source: boolean;
        priority: number;
        enabled: boolean;
      }>;
      triggers: TriggerSpec[];
    }>(`/api/v1/campaigns/${id}/detail`),
  editCampaign: (id: number, c: Record<string, unknown>) =>
    request<Campaign>(`/api/v1/campaigns/${id}`, {
      method: "PUT",
      body: JSON.stringify(c),
    }),

  transitions: () =>
    get<{
      triggers: TriggerSpec[];
      transitions: Array<{
        id: number;
        from_campaign_id: number | null;
        from_campaign: string;
        trigger: string;
        to_campaign_id: number | null;
        to_campaign: string;
        delay_minutes: number;
        priority: number;
        enabled: boolean;
      }>;
    }>("/api/v1/transitions"),
  createTransition: (t: Record<string, unknown>) =>
    post<{ id: number }>("/api/v1/transitions", t),
  deleteTransition: (id: number) => del(`/api/v1/transitions/${id}`),
  dryRun: (id: number, phone: string) =>
    post<Array<{ step_index: number; day_offset: number; when: string; outcome: string; detail?: string }>>(
      `/api/v1/campaigns/${id}/dry-run`,
      { phone },
    ),

  keys: () => get<KeysList>("/api/v1/keys"),
  createKey: (k: { name: string; role: string; person?: string; confirm?: string }) =>
    post<IssuedKey>("/api/v1/keys", k),
  revokeKey: (id: number) => post<ApiKeyRow>(`/api/v1/keys/${id}/revoke`),
  rotateKey: (id: number) => post<IssuedKey>(`/api/v1/keys/${id}/rotate`),
  keyUsage: (id: number, days = 30) =>
    get<{ key: ApiKeyRow; days: number; rows: Array<{ day: string; tool: string; calls: number; errors: number; last_at: string | null }> }>(
      `/api/v1/keys/${id}/usage?days=${days}`,
    ),

  autopilot: () => get<AutopilotStatus>("/api/v1/autopilot"),
  autopilotPlan: (agents?: number | null, perAgentHour?: number | null) => {
    const q = new URLSearchParams();
    if (agents != null) q.set("agents", String(agents));
    if (perAgentHour != null) q.set("per_agent_hour", String(perAgentHour));
    const qs = q.toString();
    return get<AutopilotPlan>(`/api/v1/autopilot/plan${qs ? `?${qs}` : ""}`);
  },
  autopilotModel: () => get<AutopilotModel>("/api/v1/autopilot/model"),
  updateAutopilot: (c: Partial<AutopilotConfig> & { clear_override?: boolean }) =>
    patch<AutopilotConfig>("/api/v1/autopilot", c),
  autopilotPulse: (level: "behind" | "idle" | "clear", minutes = 60) =>
    post<AutopilotConfig>("/api/v1/autopilot/pulse", { level, minutes }),
  /** "volume is low": more work now, from the audience approved on the Autopilot screen */
  autopilotTopUp: (body: { texts?: number; preview?: boolean }) =>
    post<{
      texts?: number;
      cap_left?: number;
      expected_conversations?: number;
      expected_calls?: number;
      blast_id?: number;
      audience?: string;
      message?: string;
      skipped?: string;
      off?: boolean | string;
    }>("/api/v1/autopilot/top-up", body),
  autopilotLearn: () => post<{ learned_at: string; segments: number }>("/api/v1/autopilot/learn"),

  sendConfig: () => get<SendConfig>("/api/v1/send-config"),
  pace: (preview?: number) =>
    get<Pace>(`/api/v1/sending/pace${preview != null ? `?preview=${preview}` : ""}`),
  updateSendConfig: (c: Partial<SendConfig>) => patch<SendConfig>("/api/v1/send-config", c),
  killSwitch: (on: boolean, reason?: string) =>
    post<SendConfig>("/api/v1/send-config/kill", { on, reason }),

  liveStats: () => get<LiveStats>("/api/v1/stats/live"),
  history: (days = 14) =>
    get<{ available: boolean; days: HistoryDay[]; reason?: string }>(
      `/api/v1/stats/history?days=${days}`,
    ),
  byDayOffset: () =>
    get<{ available: boolean; rows: DayOffsetRow[]; reason?: string }>(
      "/api/v1/stats/by-day-offset",
    ),

  queue: () => get<{ items: QueueItem[] }>("/api/v1/queue"),

  users: () => get<User[]>("/api/v1/users"),
  addUser: (u: { email: string; role: string; name?: string }) =>
    post<User>("/api/v1/users", u),
  updateUser: (id: number, patchBody: Partial<User>) =>
    patch<User>(`/api/v1/users/${id}`, patchBody),

  createTemplate: (t: {
    key: string; name: string; variants: Array<{ variant_key: string; body: string; weight: number }>;
  }) => post<Template>("/api/v1/templates", t),
  updateVariant: (templateId: number, variantId: number, body: string) =>
    patch(`/api/v1/templates/${templateId}/variants/${variantId}`, { body }),

  importPreview: async (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(BASE + "/api/v1/contacts/import/preview", {
      method: "POST", credentials: "include", body: fd,
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json()).detail ?? res.statusText);
    return res.json();
  },
  importTargets: () =>
    get<Array<{ key: string; label: string; required?: boolean }>>(
      "/api/v1/contacts/import/targets",
    ),

  runImport: async (
    file: File,
    dryRun = false,
    mapping?: Record<string, string>,
    batchTag?: string,
    consentSource?: string,
  ) => {
    const fd = new FormData();
    fd.append("file", file);
    // the user's corrected mapping wins over whatever we guessed
    if (mapping) fd.append("mapping", JSON.stringify(mapping));
    // One tag on every row, so the audience builder has a handle on "the
    // people from that file" without a tags column pasted into each line.
    if (batchTag) fd.append("batch_tag", batchTag);
    // Consent is not importable as a column - a spreadsheet is not a consent
    // record - but naming where it came from is, and without it every row
    // lands unreachable and the import looks like it silently failed.
    if (consentSource) fd.append("consent_source", consentSource);
    const res = await fetch(BASE + `/api/v1/contacts/import?dry_run=${dryRun}`, {
      method: "POST", credentials: "include", body: fd,
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json()).detail ?? res.statusText);
    return res.json();
  },

  loginUrl: () => `${BASE}/auth/google/login`,
};


/** The volume autopilot (backend/app/engine/autopilot.py). */
export type AutopilotConfig = {
  enabled: boolean;
  /** "1".."7" (Monday..Sunday) -> 24 hourly agent counts, Eastern */
  agent_schedule: Record<string, number[]>;
  agents_override: number | null;
  agents_override_until: string | null;
  conversations_per_agent_hour: number;
  target_utilization: number;
  waiting_per_agent: number;
  lookahead_minutes: number;
  min_per_minute: number;
  cheap_below: number;
  hot_max_wait_minutes: number;
  overflow: "balance" | "finish" | "hold";
  /** "auto" or HH:MM Eastern */
  finish_by: string;
  pull_forward_minutes: number;
  overload_per_agent: number;
  measure_team: boolean;
  team_pulse: number | null;
  team_pulse_until: string | null;
  /** count the team from Aircall every minute */
  live_team: boolean;
  /** Aircall teams counted, comma-separated */
  live_team_names: string;
  live_weights: Record<string, number> | null;
  /** quiet-day cover: release from this audience when the team runs out of work */
  keep_busy: boolean;
  keep_busy_segment_id: number | null;
  keep_busy_template_id: number | null;
  keep_busy_daily_cap: number;
  keep_busy_min_idle: number;
  has_schedule: boolean;
  updated_at: string | null;
};

export type AutopilotDecisionDetail = {
  agents_now: number;
  team_can_take: number;
  keep_waiting: number;
  waiting: number;
  coming: number;
  room: number;
  calibration: number;
  calibration_raw?: number;
  metered_due: number;
  metered_due_texts?: number;
  remaining_points: number;
  minutes_to_finish: number | null;
  spare: number;
  lookahead: number;
  oldest_waiting_minutes?: number;
  conversations_today?: number;
  predicted_recent?: number;
  actual_recent?: number;
  next_hour_expected?: number;
  model_learned_at?: string | null;
  started_early?: number;
  estimated_waiting?: number;
  measured_waiting?: number | null;
  measured_share_unanswered?: number | null;
  measured_read?: number | null;
  median_answer_minutes?: number | null;
  team_pulse?: number | null;
  /** today's Aircall turnout against the shift pattern (median of the last hour) */
  live_turnout?: number | null;
  live_agents?: number;
  planned_agents?: number;
};

export type AircallStatus =
  | "available" | "in_call" | "after_call_work" | "do_not_disturb" | "offline";

export type AutopilotStatus = {
  config: AutopilotConfig;
  /** how much of the rest of today the team has nothing to do */
  keep_busy: {
    on: boolean;
    idle_conversations?: number;
    texts_needed?: number;
    standby_queued?: number;
    cap_left?: number;
    sent_today?: number;
    no_schedule?: boolean;
    error?: string;
  };
  /** the team as Aircall last showed it */
  live_team: null | {
    at: string;
    members: number;
    on_shift: number;
    counts: Partial<Record<AircallStatus, number>>;
    /** why those on do-not-disturb or offline are away: on_a_break, doing_back_office ... */
    reasons: Record<string, number> | null;
    error: string | null;
    fresh: boolean;
    used: boolean;
  };
  team_check: null | {
    at: string;
    read: number;
    unanswered: number;
    started: number;
    median_answer_minutes: number | null;
    used: boolean;
  };
  team_pulse_now: number;
  sending_speed: number;
  kill_switch: boolean;
  agents_now: number;
  capacity_per_hour: number;
  decision: null | {
    minute: string;
    mode: "room" | "metering" | "catch_up" | "off_shift";
    texts_cap: number;
    points_cap: number;
    agents: number;
    reason: string;
    detail: AutopilotDecisionDetail;
    current: boolean;
    /** worked out for the screen while it is off - not what is running */
    preview: boolean;
  };
  recent: Array<{
    minute: string;
    mode: string;
    points_cap: number;
    agents: number;
    texts: number;
    conversations_sent: number;
    waiting: number | null;
    coming: number | null;
    started_early: number;
  }>;
  due_now: Array<{ lane: "must" | "metered" | "cheap"; texts: number; conversations: number; oldest_since: string }>;
  as_of: string;
};

export type AutopilotSim = {
  policy: string;
  texts_sent: number;
  texts_left: number;
  conversations_left_unsent: number;
  peak_waiting: number;
  peak_at: string | null;
  longest_wait_minutes: number;
  average_wait_minutes: number;
  on_the_phone: number | null;
  standby_left?: number;
  never_reached: number;
  after_hours: number;
  conversations: number;
  idle_conversations: number;
  started_early: number;
  buckets: Array<{
    at: string;
    texts: number;
    conversations: number;
    team_can_take: number;
    waiting: number;
    agents: number;
  }>;
};

export type AutopilotPlan = {
  sending_day: boolean;
  has_schedule: boolean;
  as_of?: string;
  closes?: string;
  agents_now?: number;
  per_agent_hour?: number;
  texts_to_go?: number;
  by_lane?: Array<{ lane: string; texts: number; conversations: number }>;
  autopilot: AutopilotSim | null;
  fixed: AutopilotSim | null;
  standby?: { queued: number; left_unsent: number; idle_conversations: number; more_needed: number; blast_rate: number };
  fixed_rate?: number;
};

export type AutopilotModel = {
  learned_at: string | null;
  meta: { texts?: number; conversations?: number; days?: number; seconds?: number };
  rates: Array<{
    key: string;
    label: string;
    texts: number;
    conversations: number;
    rate: number;
    raw: number;
    parent: string | null;
  }>;
  reply_timing: Record<string, Record<string, number>>;
  hour_effect: Record<string, number>;
  defaults_in_use: boolean;
  runs: Array<{ learned_at: string; texts?: number; conversations?: number; seconds?: number }>;
};


/** API keys (backend/app/routes/keys.py). The secret is never in a row. */
export type ApiKeyRow = {
  id: number;
  name: string;
  prefix: string;
  role: string;
  person: string | null;
  created_by: string | null;
  created_at: string;
  last_used_at: string | null;
  use_count: number;
  revoked_at: string | null;
  active: boolean;
  stale: boolean;
  usage_30d: { calls: number; errors: number; tools: Array<{ tool: string; calls: number; errors: number }> };
};
export type KeysList = { keys: ApiKeyRow[]; roles: Record<string, string>; mcp_url: string; stale_days: number };
export type IssuedKey = ApiKeyRow & { key: string; connector_url: string; warning: string };
